import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Header from '../../../components/feature/Header';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import { supabase, generateTrackingId } from '../../../utils/supabase';
import { useAuth } from '../../../hooks/useAuth';
import LoginModal from '../../../components/feature/LoginModal';

interface ShipmentData {
  sender_name: string;
  receiver_name: string;
  origin: string;
  destination: string;
  weight: number;
  remarks?: string;
}

interface UploadResult {
  success: number;
  failed: number;
  errors: string[];
}

export default function BulkUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [previewData, setPreviewData] = useState<ShipmentData[]>([]);
  const { user, login } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Check authentication
  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    }
  }, [user]);

  const handleLogin = (userData: any) => {
    login(userData);
    setShowLoginModal(false);
  };

  // Show login modal if not authenticated
  if (!user) {
    return (
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => navigate('/admin')}
        onLogin={handleLogin}
      />
    );
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadResult(null);
      setPreviewData([]);
    }
  };

  const parseExcelFile = (file: File): Promise<ShipmentData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          let workbook: XLSX.WorkBook;
          
          if (file.name.toLowerCase().endsWith('.csv')) {
            // Handle CSV files
            const csvData = data as string;
            const lines = csvData.split('\n');
            const shipments: ShipmentData[] = [];

            // Skip header row
            for (let i = 1; i < lines.length; i++) {
              const line = lines[i].trim();
              if (!line) continue;

              const columns = line.split(',').map(col => col.trim().replace(/"/g, ''));
              
              if (columns.length >= 5) {
                const shipment: ShipmentData = {
                  sender_name: columns[0],
                  receiver_name: columns[1],
                  origin: columns[2],
                  destination: columns[3],
                  weight: parseFloat(columns[4]) || 0,
                  remarks: columns[5] || ''
                };
                
                if (shipment.sender_name && shipment.receiver_name && shipment.origin && shipment.destination && shipment.weight > 0) {
                  shipments.push(shipment);
                }
              }
            }
            resolve(shipments);
          } else {
            // Handle Excel files
            workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
            
            const shipments: ShipmentData[] = [];
            
            // Skip header row (index 0)
            for (let i = 1; i < jsonData.length; i++) {
              const row = jsonData[i];
              if (!row || row.length < 5) continue;
              
              const shipment: ShipmentData = {
                sender_name: String(row[0] || '').trim(),
                receiver_name: String(row[1] || '').trim(),
                origin: String(row[2] || '').trim(),
                destination: String(row[3] || '').trim(),
                weight: parseFloat(String(row[4] || '0')) || 0,
                remarks: String(row[5] || '').trim()
              };
              
              if (shipment.sender_name && shipment.receiver_name && shipment.origin && shipment.destination && shipment.weight > 0) {
                shipments.push(shipment);
              }
            }
            resolve(shipments);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  const handlePreview = async () => {
    if (!file) return;

    try {
      setLoading(true);
      const data = await parseExcelFile(file);
      setPreviewData(data);
      
      if (data.length === 0) {
        alert('No valid shipment data found in the file. Please check the format and try again.');
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Failed to parse file. Please check the format and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (previewData.length === 0) return;

    try {
      setLoading(true);
      const result: UploadResult = {
        success: 0,
        failed: 0,
        errors: []
      };

      for (const shipment of previewData) {
        try {
          // Generate unique tracking ID with new format
          let trackingId = generateTrackingId();
          let isUnique = false;
          let attempts = 0;
          const maxAttempts = 10;

          // Ensure tracking ID is unique
          while (!isUnique && attempts < maxAttempts) {
            const { data: existingShipment } = await supabase
              .from('shipments')
              .select('tracking_id')
              .eq('tracking_id', trackingId)
              .single();

            if (!existingShipment) {
              isUnique = true;
            } else {
              trackingId = generateTrackingId();
              attempts++;
            }
          }

          if (!isUnique) {
            throw new Error('Unable to generate unique tracking ID');
          }
          
          const { error: shipmentError } = await supabase
            .from('shipments')
            .insert([{
              tracking_id: trackingId,
              sender_name: shipment.sender_name,
              receiver_name: shipment.receiver_name,
              origin: shipment.origin,
              destination: shipment.destination,
              weight: shipment.weight,
              status: 'Created',
              remarks: shipment.remarks || null,
              created_by: user.full_name,
              created_by_email: user.email,
              created_at_timestamp: new Date().toISOString()
            }]);

          if (shipmentError) throw shipmentError;

          // Create initial update
          await supabase
            .from('shipment_updates')
            .insert([{
              tracking_id: trackingId,
              location: shipment.origin,
              status: 'Created',
              remarks: 'Shipment created via bulk upload'
            }]);

          result.success++;
        } catch (error) {
          result.failed++;
          result.errors.push(`Failed to create shipment for ${shipment.sender_name}: ${error}`);
        }
      }

      setUploadResult(result);
      setPreviewData([]);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading shipments:', error);
      alert('Failed to upload shipments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Create Excel template
    const templateData = [
      ['Sender Name', 'Receiver Name', 'Origin', 'Destination', 'Weight (kg)', 'Remarks'],
      ['John Doe', 'Jane Smith', 'Lagos', 'Valletta', 25.5, 'Fragile items'],
      ['Mike Johnson', 'Sarah Wilson', 'Abuja', 'Malta', 15.2, 'Electronics'],
      ['David Brown', 'Lisa Davis', 'Port Harcourt', 'Gozo', 30.0, 'Documents']
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shipments');
    
    // Download Excel file
    XLSX.writeFile(workbook, 'shipment_template.xlsx');
  };

  const downloadCSVTemplate = () => {
    const csvContent = 'Sender Name,Receiver Name,Origin,Destination,Weight (kg),Remarks\n' +
                      'John Doe,Jane Smith,Lagos,Valletta,25.5,Fragile items\n' +
                      'Mike Johnson,Sarah Wilson,Abuja,Malta,15.2,Electronics\n' +
                      'David Brown,Lisa Davis,Port Harcourt,Gozo,30.0,Documents';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shipment_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-4">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Homepage
                </Button>
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <i className="ri-dashboard-line mr-2"></i>
                  Login Dashboard
                </Button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Bulk Upload Shipments
              </h1>
              <p className="text-gray-600">Upload multiple shipments using Excel or CSV files</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Upload File</h2>
              
              <div className="space-y-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <i className="ri-file-upload-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-lg font-medium text-gray-900 mb-2">Choose Excel or CSV file to upload</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Supported formats: .xlsx, .xls, .csv<br/>
                    File should contain: Sender Name, Receiver Name, Origin, Destination, Weight, Remarks
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="mb-4"
                  >
                    <i className="ri-folder-open-line mr-2"></i>
                    Select File
                  </Button>
                  
                  {file && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <i className="ri-file-line mr-2"></i>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-between">
                  <div className="space-x-2">
                    <Button
                      onClick={downloadTemplate}
                      variant="outline"
                      className="text-sm"
                    >
                      <i className="ri-download-line mr-2"></i>
                      Excel Template
                    </Button>
                    <Button
                      onClick={downloadCSVTemplate}
                      variant="outline"
                      className="text-sm"
                    >
                      <i className="ri-download-line mr-2"></i>
                      CSV Template
                    </Button>
                  </div>
                  
                  <div className="space-x-4">
                    <Button
                      onClick={handlePreview}
                      disabled={!file || loading}
                      variant="outline"
                    >
                      Preview Data
                    </Button>
                    <Button
                      onClick={handleUpload}
                      disabled={previewData.length === 0 || loading}
                      loading={loading}
                    >
                      Upload Shipments
                    </Button>
                  </div>
                </div>
              </div>
            </Card>

            {previewData.length > 0 && (
              <Card className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Preview Data ({previewData.length} shipments)
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receiver</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.slice(0, 10).map((shipment, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-gray-900">{shipment.sender_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{shipment.receiver_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{shipment.origin} → {shipment.destination}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{shipment.weight} kg</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{shipment.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewData.length > 10 && (
                    <p className="text-sm text-gray-500 text-center py-3">
                      ... and {previewData.length - 10} more shipments
                    </p>
                  )}
                </div>
              </Card>
            )}

            {uploadResult && (
              <Card className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Results</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-line text-green-600 text-xl mr-3"></i>
                      <span className="text-green-800 font-medium">Successfully created: {uploadResult.success} shipments</span>
                    </div>
                  </div>
                  
                  {uploadResult.failed > 0 && (
                    <div className="p-4 bg-red-50 rounded-lg">
                      <div className="flex items-center mb-2">
                        <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                        <span className="text-red-800 font-medium">Failed: {uploadResult.failed} shipments</span>
                      </div>
                      {uploadResult.errors.length > 0 && (
                        <div className="mt-2 text-sm text-red-700">
                          <p className="font-medium mb-1">Errors:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {uploadResult.errors.slice(0, 5).map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                          {uploadResult.errors.length > 5 && (
                            <p className="mt-1">... and {uploadResult.errors.length - 5} more errors</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button
                    onClick={() => navigate('/admin')}
                    className="w-full"
                  >
                    View All Shipments
                  </Button>
                </div>
              </Card>
            )}
          </div>

          <div>
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Instructions</h3>
              <div className="space-y-4 text-sm text-gray-600">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-bold text-xs">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Download Template</p>
                    <p>Get the Excel or CSV template with the correct format and sample data.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-bold text-xs">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Fill Your Data</p>
                    <p>Add your shipment details following the template format in Excel or CSV.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-bold text-xs">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Upload & Preview</p>
                    <p>Upload your file and preview the data before final submission.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-bold text-xs">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Create Shipments</p>
                    <p>Confirm to create all shipments with tracking IDs.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-medium text-yellow-800 mb-2">Required Columns:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Sender Name</li>
                  <li>• Receiver Name</li>
                  <li>• Origin</li>
                  <li>• Destination</li>
                  <li>• Weight (kg)</li>
                  <li>• Remarks (optional)</li>
                </ul>
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Supported Formats:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Excel files (.xlsx, .xls)</li>
                  <li>• CSV files (.csv)</li>
                  <li>• Maximum file size: 10MB</li>
                  <li>• Maximum 1000 shipments per upload</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
